import { useMessageStore } from "@/state/stores";
import { useEffect } from "react";
import { useDeployment } from "./useDeployment";

/**
 * Hook to initialize and manage deployment logs for a specific deployment.
 * Fetches initial deployment data and populates the logs store.
 */
export function useDeploymentLogs(chatId?: string, deploymentId?: string) {
  const { data: deploymentData, isLoading } = useDeployment(
    chatId,
    deploymentId,
  );
  const setDeploymentLogs = useMessageStore((state) => state.setDeploymentLogs);

  // Initialize logs in the store when deployment data is available
  useEffect(() => {
    if (!deploymentId || !deploymentData?.data?.deployment) {
      return;
    }

    const deployment = deploymentData.data.deployment;
    const stepsLogs: Record<string, string> = {};

    // Extract logs from deployment steps
    for (const step of deployment.steps) {
      stepsLogs[step.id] = step.logs || "";
    }

    // Set logs in the store
    setDeploymentLogs(deploymentId, stepsLogs);
  }, [deploymentId, deploymentData, setDeploymentLogs]);

  return {
    isLoading,
    deployment: deploymentData?.data?.deployment,
  };
}

/**
 * Hook to get logs for a specific deployment step.
 * Returns logs that update in real-time via WebSocket events.
 */
export function useStepLogs(deploymentId?: string, stepId?: string) {
  return useMessageStore((state) => {
    if (!deploymentId || !stepId) {
      return "";
    }

    return state.deploymentLogs[deploymentId]?.[stepId]?.logs || "";
  });
}
